import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Grid,
} from "@mui/material";
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { useQueryClient } from "@tanstack/react-query";
import {
  branchAPI,
  CreateBranchData,
  UpdateBranchData,
} from "../../services/branchAPI";
import { useRBAC } from "../../hooks/useRBAC";

const BranchFormPage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canUpdate } = useRBAC();
  const isCreateMode = location.pathname.endsWith("/new");
  const isEditMode = Boolean(id) && location.pathname.endsWith("/edit");
  const isViewMode = Boolean(id) && !isCreateMode && !isEditMode;
  const readOnly = isViewMode || (isEditMode && !canUpdate("branches"));

  const [formData, setFormData] = useState<CreateBranchData>({
    name: "",
    address: "",
    phone: "",
    email: "",
  });
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!(isEditMode || isViewMode) || !id) return;

    const fetchBranch = async () => {
      try {
        setLoading(true);
        const branch = await branchAPI.getBranchById(id);
        setFormData({
          name: branch.name || "",
          address: branch.address || "",
          phone: branch.phone || "",
          email: branch.email || "",
        });
        setIsActive(branch.isActive);
      } catch (err: any) {
        setError(err.response?.data?.message || "Failed to fetch branch");
      } finally {
        setLoading(false);
      }
    };

    void fetchBranch();
  }, [id, isEditMode, isViewMode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError("Branch name is required");
      return;
    }
    if (!formData.address.trim()) {
      setError("Branch address is required");
      return;
    }
    if (!formData.phone.trim()) {
      setError("Branch phone is required");
      return;
    }
    if (!formData.email.trim()) {
      setError("Branch email is required");
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (isEditMode) {
        await branchAPI.updateBranch(id!, {
          ...formData,
          isActive,
        } as UpdateBranchData);
        await queryClient.invalidateQueries({ queryKey: ["branch", id] });
      } else {
        await branchAPI.createBranch(formData);
      }

      await queryClient.invalidateQueries({ queryKey: ["branches"] });
      navigate("/branches");
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to save branch");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/branches")}
          sx={{ mr: 2 }}
        >
          Back to Branches
        </Button>
        <Typography variant="h4" component="h1" sx={{ flex: 1 }}>
          {isViewMode
            ? "View Branch"
            : isEditMode
              ? "Edit Branch"
              : "Add New Branch"}
        </Typography>
        {isViewMode && canUpdate("branches") && id && (
          <Button
            variant="contained"
            onClick={() => navigate(`/branches/${id}/edit`)}
          >
            Edit
          </Button>
        )}
      </Box>

      <Paper elevation={2}>
        <Box p={3}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Branch Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required={!readOnly}
                  disabled={readOnly}
                  helperText="Enter a unique branch name"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required={!readOnly}
                  disabled={readOnly}
                  helperText="Enter branch contact number"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required={!readOnly}
                  disabled={readOnly}
                  helperText="Enter branch email address"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  required={!readOnly}
                  disabled={readOnly}
                  helperText="Enter complete branch address"
                />
              </Grid>

              {(isEditMode || isViewMode) && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isActive}
                        onChange={(e) => setIsActive(e.target.checked)}
                        disabled={readOnly}
                      />
                    }
                    label="Branch Active Status"
                  />
                </Grid>
              )}

              {!readOnly && (
                <Grid item xs={12}>
                  <Box display="flex" gap={2}>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<SaveIcon />}
                      disabled={saving}
                    >
                      {saving
                        ? "Saving..."
                        : isEditMode
                          ? "Update Branch"
                          : "Create Branch"}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => navigate("/branches")}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Grid>
              )}
            </Grid>
          </form>
        </Box>
      </Paper>
    </Box>
  );
};

export default BranchFormPage;
